/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { DealerContextServiceV1, UnsubscribeFn } from '@oneaudi/dealer-context-service';
import ErrorBoundary from './components/ErrorBoundary';
import { COLORS } from './styles/variables';
import GlobalStyles from './styles';
import i18n from '../i18n';
import useQueryFilters from './hooks/useQueryFilters';
import { setConfirmedFilters } from './reducers/filtersReducer';
import { setDealerContext } from './reducers/dealerContextReducer';
import { GEO_ZIP_URL_PARAM, PARTNER_ID, MARKET } from './constants/environment';
import Offers from './components';
import { ConfigTypes } from './types';
import { setConfirmedZipCode, setMarket } from './reducers/zipCodeReducer';

interface FeatureAppProps {
  // eslint-disable-next-line react/no-unused-prop-types
  configProvider?: GfaServiceConfigProviderV1;
  dealerContextService: DealerContextServiceV1;
  remoteConfig: ConfigTypes;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  dealerContextService,
  remoteConfig,
}: FeatureAppProps) => {
  const dispatch = useDispatch();

  const parsedParams = useQueryFilters();

  useEffect(() => {
    if (remoteConfig?.zipCode) dispatch(setConfirmedZipCode(remoteConfig?.zipCode));
    if (GEO_ZIP_URL_PARAM) dispatch(setConfirmedZipCode(GEO_ZIP_URL_PARAM));
  }, [dispatch, remoteConfig?.zipCode]);

  // Set the market from config file, or from URL
  useEffect(() => {
    if (remoteConfig?.market) {
      dispatch(setMarket(remoteConfig?.market));
      return;
    }
    dispatch(setMarket('USA'));
  }, [dispatch, remoteConfig?.market]);

  // Saving parsed params from URL to confirmed filters redux
  useEffect(() => {
    if (parsedParams) {
      // Filters coming from Query Params
      dispatch(setConfirmedFilters(parsedParams));
    }
  }, [dispatch, parsedParams]);

  // Saving dealerId if Delaer Context exists
  useEffect(() => {
    if (PARTNER_ID) {
      dispatch(setDealerContext({ dealerId: PARTNER_ID }));
    }
    const unsubscribeDealerContext: UnsubscribeFn = dealerContextService?.subscribe(
      (dealerContext) => {
        if (dealerContext) {
          const dealerId = dealerContext?.id;
          dispatch(setDealerContext({ dealerId }));
        }
      },
    );
    return (): void => {
      if (dealerContextService) {
        unsubscribeDealerContext();
      }
    };
  }, [dealerContextService, dispatch]);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <div className="resetWrapper">
            <Offers />
          </div>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
